/**
 * Customer Mappings & Sync Health — single admin page.
 *
 * Replaces the old Mappings + Sync Health tabs. Shows summary tiles, issue
 * alerts (missing/orphaned/unmapped/SF-unmatched), and the merged mapping
 * table with Cosmos org, Salesforce account, AE, and computed subscription
 * end date per customer.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { FiTrash2 } from 'react-icons/fi';
import Alert from './Alert';
import Button from './Button';
import CustomerSelect from './CustomerSelect';
import DeleteCustomerWizard from './DeleteCustomerWizard';
import LoadingSpinner from './LoadingSpinner';
import { useCustomers } from '../context/CustomerContext';
import { displayName } from '../utils/customerDisplay';
import './CustomerMappings.css';

// ─── Types ─────────────────────────────────────────────────────────────────

interface FuzzyMatch { name: string; score: number; }

interface SyncHealthSummary {
  total_stripe: number;
  total_mysql: number;
  total_cosmos_orgs: number;
  fully_synced: number;
  missing_from_mysql: number;
  orphaned_in_mysql: number;
  no_stripe_description: number;
  unmapped_to_cosmos: number;
  cosmos_unlinked: number;
}

interface MissingFromMysql {
  stripe_id: string;
  description: string;
  subscription_status: string | null;
  contract_units: number;
}

interface OrphanedInMysql {
  customer_id: string | null;
  customer_tag: string;
}

interface NoStripeDescription {
  stripe_id: string;
  name: string;
  email: string;
  subscription_status: string | null;
  contract_units: number;
}

interface UnmappedToCosmos {
  customer_tag: string;
  customer_id: string | null;
  suggested_matches: FuzzyMatch[];
}

interface CosmosUnlinked {
  org_name: string;
  device_count: number;
  suggested_matches: FuzzyMatch[];
}

interface SyncHealthData {
  summary: SyncHealthSummary;
  issues: {
    missing_from_mysql: MissingFromMysql[];
    orphaned_in_mysql: OrphanedInMysql[];
    no_stripe_description: NoStripeDescription[];
    unmapped_to_cosmos: UnmappedToCosmos[];
    cosmos_unlinked: CosmosUnlinked[];
  };
  all_cosmos_orgs: string[];
  all_customer_tags: string[];
  warnings?: { cosmos?: string };
}

interface SalesforceLink {
  salesforce_account_id: string;
  account_name: string | null;
  parent_account_id: string | null;
  owner_name: string | null;
  owner_email: string | null;
  match_method: 'exact' | 'loose' | 'manual';
  subscription_status: string | null;
  net_acv: number | null;
  total_units: number | null;
  customer_package: string | null;
  last_signed_opp: {
    id: string | null;
    name: string | null;
    close_date: string | null;
    term_months: number | null;
    amount: number | null;
  } | null;
}

interface SubscriptionEnd {
  date: string | null;
  source:
    | 'cancel_at'
    | 'period_end'
    | 'invoice_supply'
    | 'needs_fixing'
    | null
    | string;
  synced_at: string | null;
}

interface MappingRow {
  customer_tag: string;
  cosmos_org_name: string;
  device_count: number;
  devices: string[];
  salesforce_link: SalesforceLink | null;
  subscription_end: SubscriptionEnd;
}

interface SfSearchResult {
  id: string;
  name: string;
  owner_name: string | null;
  owner_email: string | null;
  score?: number;     // present on fuzzy suggestions
}

interface SfSyncResult {
  matched: number;
  ambiguous: { customer_tag: string; candidates: { id: string; name: string; owner_name: string | null; owner_email: string | null; }[]; }[];
  unmatched: string[];
  total_plix_customers: number;
  synced_at: string;
}

interface Organization {
  name: string;
  retentionPeriod: number;
  retentionUnit: string;
}

const HIDE_TOGGLE_KEY = 'customer-mappings:hide-fully-synced';

// Picker option — clearly separates the Salesforce account name (top) from
// the rep / Account Owner (bottom). The owner is the AE the customer will be
// associated with after picking.
const SfPickerOption: React.FC<{ item: SfSearchResult; onPick: () => void }> = ({ item, onPick }) => (
  <button className="sf-picker-option" onClick={onPick} type="button">
    <div className="sf-picker-option-top">
      <span className="sf-picker-option-name">{item.name}</span>
      {item.score != null && <span className="sf-score-badge">{item.score}% match</span>}
    </div>
    {(item.owner_name || item.owner_email) && (
      <div className="sf-picker-option-rep">
        <span className="sf-picker-option-rep-label">Rep</span>
        {item.owner_name || '—'}
        {item.owner_email && <span className="muted"> · {item.owner_email}</span>}
      </div>
    )}
  </button>
);

// ─── Component ─────────────────────────────────────────────────────────────

const CustomerMappings: React.FC = () => {
  const { customers } = useCustomers();
  const customerByTag = useMemo(
    () => new Map(customers.map(c => [c.customer_tag, c])),
    [customers],
  );
  const tagLabel = (tag: string) => {
    const c = customerByTag.get(tag);
    return c ? displayName(c) : tag;
  };

  // Mappings table state
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [customerTags, setCustomerTags] = useState<string[]>([]);
  const [newMapping, setNewMapping] = useState({ customer_tag: '', cosmos_org_name: '' });

  // Sync health state
  const [healthData, setHealthData] = useState<SyncHealthData | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);

  // Salesforce sync state
  const [sfSyncing, setSfSyncing] = useState(false);
  const [sfSyncResult, setSfSyncResult] = useState<SfSyncResult | null>(null);
  const [sfAuthError, setSfAuthError] = useState<{ message: string; detail: string } | null>(null);

  // Per-row interaction state
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [descriptionEdits, setDescriptionEdits] = useState<Record<string, string>>({});
  const [selectedMatches, setSelectedMatches] = useState<Record<string, string>>({});
  const [deleteWizardCustomer, setDeleteWizardCustomer] = useState<{ customer_tag: string; customer_id: string | null; } | null>(null);

  // SF picker state — per customer_tag
  const [sfSearchQuery, setSfSearchQuery] = useState<Record<string, string>>({});
  const [sfSearchResults, setSfSearchResults] = useState<Record<string, SfSearchResult[]>>({});
  const [sfPickerOpen, setSfPickerOpen] = useState<string | null>(null);
  const [sfSuggestions, setSfSuggestions] = useState<Record<string, SfSearchResult[]>>({});
  const [sfSuggestionsLoading, setSfSuggestionsLoading] = useState<Record<string, boolean>>({});

  // UI prefs
  const [hideSynced, setHideSynced] = useState(() => localStorage.getItem(HIDE_TOGGLE_KEY) !== 'false');
  useEffect(() => { localStorage.setItem(HIDE_TOGGLE_KEY, String(hideSynced)); }, [hideSynced]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ─── Fetchers ──────────────────────────────────────────────────────────

  const fetchMappings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/customer-mappings`);
      if (!res.ok) throw new Error('Failed to fetch mappings');
      const data = await res.json();
      setMappings(data.mappings || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch mappings');
    }
  }, []);

  const fetchHealth = useCallback(async (force = false) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sync-health${force ? '?force=true' : ''}`);
      if (!res.ok) throw new Error('Failed to fetch sync health');
      setHealthData(await res.json());
    } catch (err) {
      setHealthError(err instanceof Error ? err.message : 'Sync health unavailable');
    }
  }, []);

  const fetchCustomerTags = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/customer-tags`);
      if (res.ok) setCustomerTags(await res.json());
    } catch {/* non-fatal */}
  };

  const fetchOrganizations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/mocha/organizations`);
      if (res.ok) setOrganizations((await res.json()).organizations);
    } catch {/* non-fatal */}
  };

  useEffect(() => {
    Promise.all([fetchMappings(), fetchHealth(), fetchCustomerTags(), fetchOrganizations()])
      .finally(() => setLoading(false));
  }, [fetchMappings, fetchHealth]);

  // ─── Salesforce sync ──────────────────────────────────────────────────

  const runSalesforceSync = async () => {
    setSfSyncing(true);
    setSfAuthError(null);
    setActionError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/salesforce/sync`, { method: 'POST' });
      if (res.status === 401) {
        const err = await res.json();
        setSfAuthError({
          message: err.error || 'Salesforce login required',
          detail: err.detail || 'Run `sf org login web --alias plix` on the host.',
        });
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Sync failed (${res.status})`);
      }
      setSfSyncResult(await res.json());
      await Promise.all([fetchMappings(), fetchHealth(true)]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'SF sync failed');
    } finally {
      setSfSyncing(false);
    }
  };

  // ─── Issue handlers (carried from SyncHealthDashboard) ────────────────

  const toggleSection = (key: string) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSyncSingle = async (stripeId: string) => {
    setActionLoading(stripeId);
    setActionError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sync-health/sync-single`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripe_id: stripeId }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Sync failed');
      await Promise.all([fetchMappings(), fetchHealth(true)]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetDescription = async (stripeId: string, fallbackName: string) => {
    const description = (descriptionEdits[stripeId] ?? fallbackName).trim();
    if (!description) return;
    setActionLoading(stripeId);
    setActionError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sync-health/stripe-description`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripe_id: stripeId, description }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to set description');
      await handleSyncSingle(stripeId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed');
      setActionLoading(null);
    }
  };

  const handleCreateMapping = async (customerTag: string, cosmosOrgName: string, loadingKey?: string) => {
    if (!cosmosOrgName || !customerTag) return;
    setActionLoading(loadingKey || customerTag);
    setActionError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/customer-mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_tag: customerTag, cosmos_org_name: cosmosOrgName }),
      });
      if (res.status === 409) {
        if (!window.confirm(`"${customerTag}" already has a mapping. Replace it with "${cosmosOrgName}"?`)) return;
        await fetch(`${API_BASE_URL}/api/customer-mappings/${encodeURIComponent(customerTag)}`, { method: 'DELETE' });
        const retry = await fetch(`${API_BASE_URL}/api/customer-mappings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer_tag: customerTag, cosmos_org_name: cosmosOrgName }),
        });
        if (!retry.ok) throw new Error((await retry.json()).error || 'Replace failed');
      } else if (!res.ok) {
        throw new Error((await res.json()).error || 'Failed to create mapping');
      }
      await Promise.all([fetchMappings(), fetchHealth(true)]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteOrphan = async (customerTag: string) => {
    if (!window.confirm(`Delete "${customerTag}" from the inventory database? This cannot be undone.`)) return;
    setActionLoading(customerTag);
    setActionError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/customers/${encodeURIComponent(customerTag)}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json();
        // Anything the wizard can resolve should open the wizard. Keyed off the
        // shape of the response rather than exact error prose: `outstanding` is
        // returned whenever units still need discharging, and `stripe_id`
        // whenever Stripe is the blocker. Matching on message text meant a
        // reworded gate silently stopped launching the wizard.
        const wizardCanFix =
          Array.isArray(err.outstanding) ||
          err.stripe_id ||
          err.error === 'Customer still exists in Stripe';
        if (wizardCanFix) {
          setDeleteWizardCustomer({ customer_tag: customerTag, customer_id: err.stripe_id || null });
          return;
        }
        throw new Error(err.error || 'Delete failed');
      }
      await Promise.all([fetchMappings(), fetchHealth(true)]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteMapping = async (customerTag: string) => {
    setActionLoading(`map-${customerTag}`);
    try {
      const res = await fetch(`${API_BASE_URL}/api/customer-mappings/${encodeURIComponent(customerTag)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete mapping');
      await Promise.all([fetchMappings(), fetchHealth(true)]);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setActionLoading(null);
    }
  };

  // ─── New mapping form ─────────────────────────────────────────────────

  const handleSubmitMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMapping.customer_tag || !newMapping.cosmos_org_name) return;
    await handleCreateMapping(newMapping.customer_tag, newMapping.cosmos_org_name);
    setNewMapping({ customer_tag: '', cosmos_org_name: '' });
  };

  // ─── Salesforce link handlers ─────────────────────────────────────────

  // Optimistic link: close the picker immediately, patch the row in-place from
  // the picker's display fields, then PUT in the background. The backend skips
  // its own SOQL when the body has name/owner — round-trip is one DB UPDATE.
  const handleSetSfLink = (customerTag: string, account: SfSearchResult) => {
    const previous = mappings.find(m => m.customer_tag === customerTag)?.salesforce_link ?? null;
    const optimistic: SalesforceLink = {
      salesforce_account_id: account.id,
      account_name: account.name,
      parent_account_id: previous?.parent_account_id ?? null,
      owner_name: account.owner_name ?? null,
      owner_email: account.owner_email ?? null,
      match_method: 'manual',
      subscription_status: previous?.subscription_status ?? null,
      net_acv: previous?.net_acv ?? null,
      total_units: previous?.total_units ?? null,
      customer_package: previous?.customer_package ?? null,
      last_signed_opp: previous?.salesforce_account_id === account.id ? (previous?.last_signed_opp ?? null) : null,
    };
    setMappings(prev => prev.map(m => m.customer_tag === customerTag
      ? { ...m, salesforce_link: optimistic } : m));
    setSfPickerOpen(null);
    setActionError(null);

    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/admin/salesforce/links/${encodeURIComponent(customerTag)}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              salesforce_account_id: account.id,
              account_name: account.name,
              owner_name: account.owner_name,
              owner_email: account.owner_email,
            }),
          },
        );
        if (res.status === 401) {
          const err = await res.json();
          setSfAuthError({ message: err.error, detail: err.detail });
          // Roll back the optimistic update
          setMappings(prev => prev.map(m => m.customer_tag === customerTag
            ? { ...m, salesforce_link: previous } : m));
          return;
        }
        if (!res.ok) throw new Error((await res.json()).error || 'Failed to link');
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to link');
        setMappings(prev => prev.map(m => m.customer_tag === customerTag
          ? { ...m, salesforce_link: previous } : m));
      }
    })();
  };

  const handleClearSfLink = async (customerTag: string) => {
    if (!window.confirm(`Clear Salesforce link for "${customerTag}"? It'll re-auto-match on next sync.`)) return;
    setActionLoading(`sf-${customerTag}`);
    try {
      await fetch(
        `${API_BASE_URL}/api/admin/salesforce/links/${encodeURIComponent(customerTag)}`,
        { method: 'DELETE' },
      );
      await fetchMappings();
    } finally {
      setActionLoading(null);
    }
  };

  // Debounce SF picker keystrokes — fire one search 300ms after the user stops
  // typing instead of one per character (the live SOQL hop is ~500ms/round trip).
  const sfSearchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const sfSearchSeq = useRef<Record<string, number>>({});
  const [sfSearchLoading, setSfSearchLoading] = useState<Record<string, boolean>>({});

  const searchSfAccounts = (customerTag: string, q: string) => {
    setSfSearchQuery(prev => ({ ...prev, [customerTag]: q }));
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setSfSearchResults(prev => ({ ...prev, [customerTag]: [] }));
      setSfSearchLoading(prev => ({ ...prev, [customerTag]: false }));
      const t = sfSearchTimers.current[customerTag];
      if (t) clearTimeout(t);
      return;
    }
    const prev = sfSearchTimers.current[customerTag];
    if (prev) clearTimeout(prev);
    setSfSearchLoading(prev => ({ ...prev, [customerTag]: true }));
    const seq = (sfSearchSeq.current[customerTag] || 0) + 1;
    sfSearchSeq.current[customerTag] = seq;
    sfSearchTimers.current[customerTag] = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/admin/salesforce/accounts/search?q=${encodeURIComponent(trimmed)}`,
        );
        if (res.status === 401) {
          const err = await res.json();
          setSfAuthError({ message: err.error, detail: err.detail });
          return;
        }
        if (res.ok && sfSearchSeq.current[customerTag] === seq) {
          // Only apply this response if it's still the latest in-flight search
          const data = await res.json();
          setSfSearchResults(prev => ({ ...prev, [customerTag]: data.accounts || [] }));
        }
      } catch {/* non-fatal */}
      finally {
        if (sfSearchSeq.current[customerTag] === seq) {
          setSfSearchLoading(prev => ({ ...prev, [customerTag]: false }));
        }
      }
    }, 300);
  };

  useEffect(() => {
    return () => {
      Object.values(sfSearchTimers.current).forEach(t => t && clearTimeout(t));
    };
  }, []);

  const openSfPicker = useCallback(async (customerTag: string) => {
    setSfPickerOpen(customerTag);
    if (sfSuggestions[customerTag]) return;     // already loaded
    setSfSuggestionsLoading(prev => ({ ...prev, [customerTag]: true }));
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/salesforce/suggest?customer_tag=${encodeURIComponent(customerTag)}`,
      );
      if (res.status === 401) {
        const err = await res.json();
        setSfAuthError({ message: err.error, detail: err.detail });
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setSfSuggestions(prev => ({ ...prev, [customerTag]: data.suggestions || [] }));
      }
    } catch {/* non-fatal */}
    finally {
      setSfSuggestionsLoading(prev => ({ ...prev, [customerTag]: false }));
    }
  }, [sfSuggestions]);

  // ─── Helpers ───────────────────────────────────────────────────────────

  const ambiguousByTag = useMemo(() => {
    const m: Record<string, SfSyncResult['ambiguous'][number]['candidates']> = {};
    sfSyncResult?.ambiguous.forEach(a => { m[a.customer_tag] = a.candidates; });
    return m;
  }, [sfSyncResult]);

  // What action (if any) does this row need? Surfaced in a single column on
  // the admin table — subscription/contract details live on /customers, not here.
  const actionFor = useCallback((m: MappingRow): { label: string; severity: 'warn' | 'urgent' } | null => {
    if (!m.cosmos_org_name) return { label: 'Map a Cosmos org', severity: 'urgent' };
    const sf = m.salesforce_link;
    const ambiguous = ambiguousByTag[m.customer_tag];
    if (!sf && ambiguous && ambiguous.length > 1) return { label: `Resolve ${ambiguous.length} SF candidates`, severity: 'warn' };
    if (!sf) return { label: 'Pick SF account', severity: 'warn' };
    return null;
  }, [ambiguousByTag]);

  const visibleMappings = useMemo(
    () => hideSynced ? mappings.filter(m => actionFor(m) !== null) : mappings,
    [mappings, hideSynced, actionFor],
  );

  const sfMatchedCount = mappings.filter(m => m.salesforce_link).length;
  const sfManualCount = mappings.filter(m => m.salesforce_link?.match_method === 'manual').length;
  const actionCount = mappings.filter(m => actionFor(m) !== null).length;

  // ─── Render ────────────────────────────────────────────────────────────

  if (loading) return <LoadingSpinner message="Loading mappings & sync health…" />;

  const summary = healthData?.summary;
  const issues = healthData?.issues;

  return (
    <div className="customer-mappings">
      <div className="page-header">
        <h2>Customer Mappings &amp; Sync Health</h2>
        <div className="page-header-actions">
          <label className="hide-toggle">
            <input type="checkbox" checked={hideSynced} onChange={e => setHideSynced(e.target.checked)} />
            Show only rows needing action ({actionCount})
          </label>
          <Button variant="primary" size="sm" loading={sfSyncing} onClick={runSalesforceSync}>
            {sfSyncing ? 'Syncing…' : 'Sync from Salesforce'}
          </Button>
          <Button variant="primary" size="sm" onClick={() => fetchHealth(true)}>↻ Refresh from Stripe</Button>
        </div>
      </div>

      {sfAuthError && (
        <Alert type="error" onDismiss={() => setSfAuthError(null)}>
          <strong>{sfAuthError.message}.</strong> {sfAuthError.detail}
        </Alert>
      )}
      {actionError && <Alert type="error" onDismiss={() => setActionError(null)}>{actionError}</Alert>}
      {error && <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>}
      {healthError && <Alert type="warning">{healthError}</Alert>}
      {healthData?.warnings?.cosmos && (
        <Alert type="warning">CosmosDB unavailable: {healthData.warnings.cosmos}</Alert>
      )}

      {sfSyncResult && (
        <Alert type="success" onDismiss={() => setSfSyncResult(null)}>
          SF synced: {sfSyncResult.matched} matched / {sfSyncResult.ambiguous.length} ambiguous /{' '}
          {sfSyncResult.unmatched.length} unmatched (of {sfSyncResult.total_plix_customers} customers)
        </Alert>
      )}

      {/* Summary tiles */}
      {summary && (
        <div className="summary-cards">
          <div className="summary-card green">
            <div className="card-number">{summary.fully_synced}</div>
            <div className="card-label">Fully Synced</div>
          </div>
          <div className={`summary-card ${summary.missing_from_mysql > 0 ? 'red' : 'neutral'}`}>
            <div className="card-number">{summary.missing_from_mysql}</div>
            <div className="card-label">Missing from MySQL</div>
          </div>
          <div className={`summary-card ${summary.orphaned_in_mysql > 0 ? 'orange' : 'neutral'}`}>
            <div className="card-number">{summary.orphaned_in_mysql}</div>
            <div className="card-label">Orphaned in MySQL</div>
          </div>
          <div className={`summary-card ${summary.no_stripe_description > 0 ? 'yellow' : 'neutral'}`}>
            <div className="card-number">{summary.no_stripe_description}</div>
            <div className="card-label">No Description</div>
          </div>
          <div className={`summary-card ${summary.unmapped_to_cosmos > 0 ? 'blue' : 'neutral'}`}>
            <div className="card-number">{summary.unmapped_to_cosmos}</div>
            <div className="card-label">Unmapped (Cosmos)</div>
          </div>
          <div className={`summary-card ${(mappings.length - sfMatchedCount) > 0 ? 'blue' : 'neutral'}`}>
            <div className="card-number">{sfMatchedCount}/{mappings.length}</div>
            <div className="card-label">SF linked ({sfManualCount} manual)</div>
          </div>
        </div>
      )}

      {/* Issue sections */}
      {issues && issues.missing_from_mysql.length > 0 && (
        <div className="issue-section">
          <button className="section-header" onClick={() => toggleSection('missing')}>
            <span className="section-title red-text">Missing from MySQL ({issues.missing_from_mysql.length})</span>
            <span className="toggle-icon">{expandedSections['missing'] !== false ? '▼' : '▶'}</span>
          </button>
          {expandedSections['missing'] !== false && (
            <table className="issue-table">
              <thead><tr><th>Stripe ID</th><th>Description</th><th>Status</th><th>Units</th><th>Action</th></tr></thead>
              <tbody>
                {issues.missing_from_mysql.map(item => (
                  <tr key={item.stripe_id}>
                    <td className="mono">{item.stripe_id}</td>
                    <td>{item.description}</td>
                    <td><span className={`status-badge ${item.subscription_status}`}>{item.subscription_status || 'none'}</span></td>
                    <td>{item.contract_units}</td>
                    <td><Button size="sm" loading={actionLoading === item.stripe_id} onClick={() => handleSyncSingle(item.stripe_id)}>Sync</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {issues && issues.orphaned_in_mysql.length > 0 && (
        <div className="issue-section">
          <button className="section-header" onClick={() => toggleSection('orphaned')}>
            <span className="section-title orange-text">Orphaned in MySQL ({issues.orphaned_in_mysql.length})</span>
            <span className="toggle-icon">{expandedSections['orphaned'] !== false ? '▼' : '▶'}</span>
          </button>
          {expandedSections['orphaned'] !== false && (
            <table className="issue-table">
              <thead><tr><th>Customer Tag</th><th>Stripe ID</th><th>Action</th></tr></thead>
              <tbody>
                {issues.orphaned_in_mysql.map(item => (
                  <tr key={item.customer_tag}>
                    <td>{item.customer_tag}</td>
                    <td className="mono">{item.customer_id || '—'}</td>
                    <td>
                      {item.customer_id ? (
                        <Button variant="danger" size="sm" loading={actionLoading === item.customer_tag}
                          onClick={() => setDeleteWizardCustomer({ customer_tag: item.customer_tag, customer_id: item.customer_id })}>
                          Delete
                        </Button>
                      ) : (
                        <Button variant="danger" size="sm" loading={actionLoading === item.customer_tag}
                          onClick={() => handleDeleteOrphan(item.customer_tag)}>
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {issues && issues.no_stripe_description.length > 0 && (
        <div className="issue-section">
          <button className="section-header" onClick={() => toggleSection('nodesc')}>
            <span className="section-title yellow-text">No Stripe Description ({issues.no_stripe_description.length})</span>
            <span className="toggle-icon">{expandedSections['nodesc'] !== false ? '▼' : '▶'}</span>
          </button>
          {expandedSections['nodesc'] !== false && (
            <table className="issue-table">
              <thead><tr><th>Stripe ID</th><th>Name</th><th>Email</th><th>Description</th><th>Action</th></tr></thead>
              <tbody>
                {issues.no_stripe_description.map(item => (
                  <tr key={item.stripe_id}>
                    <td className="mono">{item.stripe_id}</td>
                    <td>{item.name}</td>
                    <td>{item.email}</td>
                    <td>
                      <input type="text" className="description-input" placeholder={item.name}
                        value={descriptionEdits[item.stripe_id] ?? item.name}
                        onChange={e => setDescriptionEdits(prev => ({ ...prev, [item.stripe_id]: e.target.value }))} />
                    </td>
                    <td><Button size="sm" loading={actionLoading === item.stripe_id} onClick={() => handleSetDescription(item.stripe_id, item.name)}>Save &amp; Sync</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {issues && issues.unmapped_to_cosmos.length > 0 && (
        <div className="issue-section">
          <button className="section-header" onClick={() => toggleSection('unmapped')}>
            <span className="section-title blue-text">Unmapped to Cosmos ({issues.unmapped_to_cosmos.length})</span>
            <span className="toggle-icon">{expandedSections['unmapped'] !== false ? '▼' : '▶'}</span>
          </button>
          {expandedSections['unmapped'] !== false && (
            <table className="issue-table">
              <thead><tr><th>Customer</th><th>Cosmos Org</th><th>Devices</th><th>Action</th></tr></thead>
              <tbody>
                {issues.unmapped_to_cosmos.map(item => (
                  <tr key={`cust-${item.customer_tag}`}>
                    <td title={item.customer_tag}>{tagLabel(item.customer_tag)}</td>
                    <td>
                      <select className="match-select"
                        value={selectedMatches[item.customer_tag] || ''}
                        onChange={e => setSelectedMatches(prev => ({ ...prev, [item.customer_tag]: e.target.value }))}>
                        <option value="">— Select org —</option>
                        {item.suggested_matches.length > 0 && (
                          <optgroup label="Suggestions">
                            {item.suggested_matches.map(m => <option key={m.name} value={m.name}>{m.name} ({m.score}%)</option>)}
                          </optgroup>
                        )}
                        <optgroup label="All orgs">
                          {(healthData?.all_cosmos_orgs || []).map(org => <option key={org} value={org}>{org}</option>)}
                        </optgroup>
                      </select>
                    </td>
                    <td>—</td>
                    <td className="action-buttons">
                      <Button size="sm" disabled={!selectedMatches[item.customer_tag]} loading={actionLoading === item.customer_tag}
                        onClick={() => handleCreateMapping(item.customer_tag, selectedMatches[item.customer_tag])}>Link</Button>
                      <Button variant="danger" size="sm" loading={actionLoading === `delete-${item.customer_tag}`}
                        onClick={() => setDeleteWizardCustomer({ customer_tag: item.customer_tag, customer_id: item.customer_id })}>Delete</Button>
                    </td>
                  </tr>
                ))}
                {/* cosmos_unlinked rows hidden — those are orgs to delete in
                    Cosmos directly, not link to a customer. */}
              </tbody>
            </table>
          )}
        </div>
      )}

      {sfSyncResult && sfSyncResult.unmatched.length > 0 && (
        <div className="issue-section">
          <button className="section-header" onClick={() => toggleSection('sf-unmatched')}>
            <span className="section-title blue-text">Unmatched in Salesforce ({sfSyncResult.unmatched.length})</span>
            <span className="toggle-icon">{expandedSections['sf-unmatched'] ? '▼' : '▶'}</span>
          </button>
          {expandedSections['sf-unmatched'] && (
            <ul className="muted-list">
              {sfSyncResult.unmatched.map(t => <li key={t}>{tagLabel(t)}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Mappings table — main view */}
      <div className="mappings-table-container">
        <div className="mappings-table-meta">
          Showing {visibleMappings.length} of {mappings.length} mappings
          {hideSynced && mappings.length > visibleMappings.length && (
            <> · <button className="link-btn" onClick={() => setHideSynced(false)}>show all</button></>
          )}
        </div>
        <table className="mappings-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Cosmos Org</th>
              <th>Salesforce Account</th>
              <th>Action Needed</th>
              <th>Devices</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleMappings.map(m => {
              const sf = m.salesforce_link;
              const candidates = ambiguousByTag[m.customer_tag];
              return (
                <tr key={m.customer_tag}>
                  <td title={m.customer_tag}>{tagLabel(m.customer_tag)}</td>
                  <td>{m.cosmos_org_name}</td>
                  <td>
                    {sf ? (
                      <div className="sf-cell">
                        <div className="sf-cell-name">
                          {sf.account_name}
                          <span className={`sf-badge sf-badge-${sf.match_method}`}>{sf.match_method}</span>
                        </div>
                        {sf.owner_name && (
                          <div className="muted">
                            {sf.owner_name}
                            {sf.owner_email && <> · <a href={`mailto:${sf.owner_email}`}>{sf.owner_email}</a></>}
                          </div>
                        )}
                        <button className="link-btn small" onClick={() => handleClearSfLink(m.customer_tag)}>change</button>
                      </div>
                    ) : sfPickerOpen === m.customer_tag ? (
                      <div className="sf-picker">
                        {candidates && candidates.length > 0 && (
                          <div className="sf-picker-section">
                            <div className="sf-picker-label">Same name in Salesforce — pick one</div>
                            {candidates.map(c => <SfPickerOption key={c.id} item={c} onPick={() => handleSetSfLink(m.customer_tag, c)} />)}
                          </div>
                        )}
                        <div className="sf-picker-section">
                          <div className="sf-picker-label">Suggested matches</div>
                          {sfSuggestionsLoading[m.customer_tag] && <div className="muted small">Computing…</div>}
                          {!sfSuggestionsLoading[m.customer_tag] && (sfSuggestions[m.customer_tag] || []).length === 0 && (
                            <div className="muted small">No close matches — search below.</div>
                          )}
                          {(sfSuggestions[m.customer_tag] || []).map(s =>
                            <SfPickerOption key={s.id} item={s} onPick={() => handleSetSfLink(m.customer_tag, s)} />
                          )}
                        </div>
                        <input type="text" className="sf-picker-input" placeholder="Or search SF accounts…"
                          value={sfSearchQuery[m.customer_tag] || ''}
                          onChange={e => searchSfAccounts(m.customer_tag, e.target.value)} />
                        <div className="sf-picker-results">
                          {sfSearchLoading[m.customer_tag] && <div className="muted small">Searching…</div>}
                          {!sfSearchLoading[m.customer_tag] && (sfSearchResults[m.customer_tag] || []).map(r =>
                            <SfPickerOption key={r.id} item={r} onPick={() => handleSetSfLink(m.customer_tag, r)} />
                          )}
                        </div>
                        <button className="link-btn small" onClick={() => setSfPickerOpen(null)}>cancel</button>
                      </div>
                    ) : (
                      <button className="link-btn"
                        onClick={() => openSfPicker(m.customer_tag)}>
                        {candidates && candidates.length > 1 ? `${candidates.length} candidates ▾` : 'Pick…'}
                      </button>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const a = actionFor(m);
                      if (!a) return <span className="muted">—</span>;
                      return <span className={`action-badge action-${a.severity}`}>{a.label}</span>;
                    })()}
                  </td>
                  <td>{m.device_count}</td>
                  <td>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteMapping(m.customer_tag)}
                      loading={actionLoading === `map-${m.customer_tag}`} title="Delete mapping">
                      <FiTrash2 />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {visibleMappings.length === 0 && (
              <tr><td colSpan={6} className="empty-state">
                {hideSynced ? 'No mappings need action — toggle the filter off to see all rows.' : 'No customer mappings found'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create new mapping form */}
      <form onSubmit={handleSubmitMapping} className="mapping-form">
        <h3>Create new mapping</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="customer_tag">Customer Tag:</label>
            <CustomerSelect
              id="customer_tag"
              value={newMapping.customer_tag}
              onChange={(val) => setNewMapping(prev => ({ ...prev, customer_tag: val }))}
              options={customerTags
                .filter(t => !mappings.some(m => m.customer_tag === t))
                .map(t => ({ value: t, label: tagLabel(t) }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              placeholder="Search customer tags..."
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="cosmos_org_name">Cosmos Organization:</label>
            <select id="cosmos_org_name"
              value={newMapping.cosmos_org_name}
              onChange={e => setNewMapping(prev => ({ ...prev, cosmos_org_name: e.target.value }))}
              required>
              <option value="">Select an organization</option>
              {organizations
                .filter(o => !mappings.some(m => m.cosmos_org_name === o.name))
                .map(o => <option key={o.name} value={o.name}>{o.name}</option>)}
            </select>
          </div>
          <Button type="submit">Create Mapping</Button>
        </div>
      </form>

      {deleteWizardCustomer && (
        <DeleteCustomerWizard
          customerTag={deleteWizardCustomer.customer_tag}
          customerId={deleteWizardCustomer.customer_id}
          onClose={() => setDeleteWizardCustomer(null)}
          onComplete={() => {
            setDeleteWizardCustomer(null);
            fetchMappings();
            fetchHealth(true);
          }}
        />
      )}
    </div>
  );
};

export default CustomerMappings;
